/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/main.tsx
 *
 * Rôle :
 *   Point d'entrée React de l'app PWA TNE 2026.
 *   - Hydrate le `<div id="root">` du `index.html`.
 *   - Injecte le Provider TanStack Query (cache HTTP côté client).
 *   - Injecte le Provider react-intl (locale fr-FR forcée, cf. §15.5).
 *   - Enregistre le Service Worker (vite-plugin-pwa) et propose à
 *     l'utilisateur de mettre à jour quand une nouvelle version est dispo.
 *   - Retire le préloader HTML une fois React monté (cohérence visuelle).
 *
 * Réutilisable :
 *   Ce bootstrap est un template propre pour toute future PWA React DRANE.
 *   Les seuls éléments à changer : import du `App` racine, valeur de
 *   `<IntlProvider locale=…>` si la langue change un jour.
 *
 * Voir aussi : CLAUDE.md §16 (stack), §15.5 (français strict), §17 (Mika).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { LexiconIntlProvider } from './context/LexiconIntlProvider';
import { registerServiceWorker } from './pwa/registerServiceWorker';
import { surveillerLaVersion } from './pwa/versionWatch';
import { retirerParametreMaj } from './pwa/rechargerVersionRecente';
import { surveillerLeChargementDesModules } from './pwa/chargementModule';

// 2026-10-07 : polices servies par l'application (public/fonts), plus par
// Google Fonts. Mikael - chef de projet - 2026-10-07
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/globals.css';

// ──────────────────────────────────────────────────────────────────────────
// Constantes, défauts opinionatés mais surchargés par variables d'env Vite
// (VITE_QUERY_STALE_MS, VITE_QUERY_GC_MS) pour qu'on puisse ajuster en prod
// sans rebuild si nécessaire (et ce, sans toucher au code).
// ──────────────────────────────────────────────────────────────────────────
const STALE_MS = Number(import.meta.env.VITE_QUERY_STALE_MS ?? 30_000);
const GC_MS = Number(import.meta.env.VITE_QUERY_GC_MS ?? 5 * 60_000);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_MS,
      gcTime: GC_MS,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Retire le préloader HTML statique injecté dans `index.html` une fois que
 * React est monté. Géré ici (et pas dans App) parce que c'est un bootstrap
 * concern, pas une responsabilité applicative.
 */
function dismissPreloader(): void {
  const node = document.getElementById('tne-preloader');
  if (!node) return;
  node.setAttribute('aria-hidden', 'true');
  // Petite animation de fade pilotée par la classe (cf. base.css) plutôt
  // qu'inline pour respecter `prefers-reduced-motion`.
  node.classList.add('tne-preloader--leaving');
  // Suppression DOM après l'animation pour libérer la mémoire.
  window.setTimeout(() => node.remove(), 350);
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  // Cas anormal, index.html doit toujours contenir <div id="root"></div>.
  // On préfère échouer bruyamment plutôt que rendre dans <body> au hasard.
  throw new Error('TNE 2026 : élément #root introuvable dans index.html.');
}

createRoot(rootEl).render(
  <StrictMode>
    {/*
      ⚠ ORDRE VOLONTAIRE (lot CIRFA 2026-08-03) : QueryClientProvider est
      désormais AU-DESSUS de la couche i18n. `LexiconIntlProvider` monte
      `IntlProvider` avec un catalogue déjà passé au lexique de l'événement
      (« atelier » → « activité »…), ce qui exige de lire la config via
      react-query. Rien entre les deux ne consomme react-intl, donc
      l'inversion est neutre. Voir context/LexiconIntlProvider.tsx.
    */}
    <QueryClientProvider client={queryClient}>
      <LexiconIntlProvider>
        <BrowserRouter>
          <App onReady={dismissPreloader} />
        </BrowserRouter>
      </LexiconIntlProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Service Worker (vite-plugin-pwa), gère la mise à jour avec confirmation
// utilisateur (cf. CLAUDE.md §8.2.3, registerType: 'prompt').
// 2026-10-07 : la réinstallation d'une version (« Vérifier les mises à
// jour ») ajoute `?maj=…` à l'adresse ; on le retire sans recharger.
// Mikael - chef de projet - 2026-10-07
retirerParametreMaj();
registerServiceWorker();
// 2026-09-20 : filet de securite sans service worker (navigation privee,
// navigateur qui le refuse) : la version servie est comparee a celle chargee.
// 2026-09-27 : un morceau d'application disparu (deploiement pendant qu'un
// onglet est ouvert) laissait un ecran BLANC et bloquait toute navigation.
// On recharge une fois, plutot que de laisser du blanc. A brancher AVANT la
// surveillance de version : le blanc arrive tout de suite, le bandeau au
// plus tot une heure apres.
surveillerLeChargementDesModules();
surveillerLaVersion();

// Sync manager hors-ligne (cf. CLAUDE.md §18), pose les listeners
// `online` / `visibilitychange` + drain initial. Lazy import pour ne pas
// retarder le 1er rendu.
void import('./offline/syncManager').then(({ startSyncManager }) => {
  void startSyncManager();
});

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Brancher Sentry / Matomo self-host plus tard (RGPD validé).
//  - Extraire `queryClient` dans `src/api/queryClient.ts` si ré-utilisé en
//    tests (Vitest préfère un client nu par test).
// Notes pour rétro-port NOLAS :
//  - NOLAS est full vanilla JS, ce bootstrap React n'y va pas tel quel.
//    Mais le pattern (registerSW + dismissPreloader) est trivialement
//    portable en JS pur.
// ──────────────────────────────────────────────────────────────────────────
